import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectGroup,
  SelectLabel,
  SelectSeparator,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { toast } from "sonner";
import {
  Loader2,
  CreditCard,
  Link,
  AlertCircle,
  RefreshCw,
  FileText,
  DollarSign,
  Banknote,
  Send,
  MessageSquare,
  Wallet,
} from "lucide-react";
import { formatCurrency } from "@/lib/format";
import {
  type PaymentMethodType,
  useProcessPayment,
  useGeneratePaymentLink,
  useGetPaymentLink,
  useGetCustomerPaymentTerms,
  useRecordNetTermsPayment,
  useRecordManualPaymentV2,
} from "@/api/payments";

interface ProcessPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  order: {
    id: string;
    orderNumber: number;
    balanceCents: number;
    paymentMethodType?: PaymentMethodType;
    placerCustomerId?: string;
  };
  onSuccess?: () => void;
}

export function ProcessPaymentDialog({
  open,
  onOpenChange,
  order,
  onSuccess,
}: ProcessPaymentDialogProps) {
  const [method, setMethod] = useState<PaymentMethodType>(
    order.paymentMethodType || "SAVED_CARD"
  );
  const [amount, setAmount] = useState(order.balanceCents);
  const [paymentLink, setPaymentLink] = useState<string | null>(null);
  const [selectedPaymentTermsId, setSelectedPaymentTermsId] = useState<
    string | null
  >(null);

  // Fetch existing payment link
  const { data: existingPaymentLink } = useGetPaymentLink(order.id);

  // Fetch customer's available payment terms
  const { data: customerPaymentTerms } = useGetCustomerPaymentTerms(
    order.placerCustomerId || ""
  );

  // Set the payment link when we get it from the API
  useEffect(() => {
    if (existingPaymentLink) {
      setPaymentLink(existingPaymentLink);
    }
  }, [existingPaymentLink]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      // If there's an existing payment link, use it
      if (existingPaymentLink) {
        setPaymentLink(existingPaymentLink);
      }
    }
  }, [open, existingPaymentLink]);

  const processPayment = useProcessPayment(order.id, {
    onSuccess: (data) => {
      // CREDIT always pays the full balance — never fall back to the stale
      // amount input, which may hold a partial value from another method.
      const paidCents =
        data.amountCents ?? (method === "CREDIT" ? order.balanceCents : amount);
      toast.success("Payment Processed", {
        description: `Successfully processed ${formatCurrency(paidCents)} payment`,
      });
      onSuccess?.();
    },
    onError: (error: unknown) => {
      const err = error as {
        response?: {
          status?: number;
          data?: { error?: string; available?: number; required?: number };
        };
      };
      const data = err.response?.data;
      if (data?.error === "CREDIT_WALLET_FROZEN") {
        toast.error("Credit Wallet Frozen", {
          description:
            "A deposit charge is under dispute — credit spending is blocked until it resolves",
        });
        return;
      }
      if (
        data?.error === "Insufficient credit balance" &&
        data.available !== undefined &&
        data.required !== undefined
      ) {
        toast.error("Insufficient Credit", {
          description: `Available ${formatCurrency(data.available)}, required ${formatCurrency(data.required)} — credit must cover the full balance`,
        });
        return;
      }
      toast.error("Payment Failed", {
        description: data?.error || "Failed to process payment",
      });
    },
  });

  const recordManualPayment = useRecordManualPaymentV2(order.id, {
    onSuccess: (data) => {
      toast.success("Invoice Created", {
        description: "Invoice has been generated and sent to the customer",
      });
      // Open the invoice URL in a new tab
      if (data.invoice?.url) {
        window.open(data.invoice.url, "_blank");
      }
      onSuccess?.();
      onOpenChange(false);
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error("Failed to Create Invoice", {
        description: err.response?.data?.error || "Failed to create invoice",
      });
    },
  });

  const recordNetTermsPayment = useRecordNetTermsPayment(order.id, {
    onSuccess: () => {
      toast.success("NET Terms Applied", {
        description: "Order marked for NET terms invoicing",
      });
      onSuccess?.();
      onOpenChange(false);
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error("Failed to Apply NET Terms", {
        description: err.response?.data?.error || "Failed to apply NET terms",
      });
    },
  });

  const generateLink = useGeneratePaymentLink(order.id, {
    onSuccess: (link) => {
      setPaymentLink(link);
      toast.success("Payment Link Generated", {
        description: "Payment link has been created successfully",
      });
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { error?: string } } };
      toast.error("Failed to Generate Link", {
        description:
          err.response?.data?.error || "Could not generate payment link",
      });
    },
  });

  const handleProcessPayment = () => {
    switch (method) {
      case "SAVED_CARD":
        // Generate Stripe payment link for card payments
        if (!paymentLink) {
          generateLink.mutate(false); // Don't force new
        }
        break;

      case "INVOICE":
        // NET Terms - requires payment terms selection
        if (selectedPaymentTermsId) {
          recordNetTermsPayment.mutate(selectedPaymentTermsId);
        } else {
          toast.error("Payment Terms Required", {
            description: "Please select NET payment terms (NET 5/30/60/90)",
          });
        }
        break;

      case "CASH":
      case "E_TRANSFER":
      case "WECHAT":
        // Record manual payment and generate invoice with instructions
        recordManualPayment.mutate({
          method: method,
          amountCents: amount, // Send the actual amount to mark as paid and trigger task creation
          notes: `Payment method: ${method}. Payment received and confirmed.`,
        });
        break;

      case "CREDIT":
        // Credit always pays the FULL remaining balance — the server ignores
        // any partial amount (full-coverage-only spend rule).
        processPayment.mutate({
          amountCents: order.balanceCents,
          method,
        });
        break;

      default:
        toast.error("Invalid Payment Method", {
          description: "Please select a valid payment method",
        });
    }
  };

  const handleCopyLink = () => {
    if (paymentLink) {
      void navigator.clipboard.writeText(paymentLink);
      toast.success("Link Copied", {
        description: "Payment link copied to clipboard",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Process Payment</DialogTitle>
          <DialogDescription>
            Process payment for ORD-{order.orderNumber}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Payment Method</Label>
            <Select
              value={method}
              onValueChange={(value) => setMethod(value as PaymentMethodType)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  <SelectLabel>Instant Payment</SelectLabel>
                  <SelectItem value="SAVED_CARD">
                    <div className="flex items-center">
                      <CreditCard className="mr-2 h-4 w-4" />
                      Credit/Debit Card (Stripe)
                    </div>
                  </SelectItem>
                </SelectGroup>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>Deferred Payment (Invoice)</SelectLabel>
                  <SelectItem value="INVOICE">
                    <div className="flex items-center">
                      <FileText className="mr-2 h-4 w-4" />
                      NET Terms (30/60/90 days)
                    </div>
                  </SelectItem>
                  <SelectItem value="CASH">
                    <div className="flex items-center">
                      <Banknote className="mr-2 h-4 w-4" />
                      Cash (Record & Invoice)
                    </div>
                  </SelectItem>
                  <SelectItem value="E_TRANSFER">
                    <div className="flex items-center">
                      <Send className="mr-2 h-4 w-4" />
                      E-Transfer (Invoice with Instructions)
                    </div>
                  </SelectItem>
                  <SelectItem value="WECHAT">
                    <div className="flex items-center">
                      <MessageSquare className="mr-2 h-4 w-4" />
                      WeChat Pay (Invoice with QR)
                    </div>
                  </SelectItem>
                </SelectGroup>
                <SelectSeparator />
                <SelectGroup>
                  <SelectLabel>Account Balance</SelectLabel>
                  <SelectItem value="CREDIT">
                    <div className="flex items-center">
                      <Wallet className="mr-2 h-4 w-4" />
                      Customer Credit Balance
                    </div>
                  </SelectItem>
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Amount</Label>
            <CurrencyInput
              value={method === "CREDIT" ? order.balanceCents : amount}
              onChange={setAmount}
              placeholder="0.00"
              max={order.balanceCents / 100}
              disabled={method === "CREDIT"}
            />
            <p className="text-sm text-muted-foreground">
              Outstanding balance: {formatCurrency(order.balanceCents)}
            </p>
          </div>

          {method === "SAVED_CARD" && paymentLink && (
            <Alert>
              <Link className="h-4 w-4" />
              <AlertDescription className="space-y-2">
                <p>Payment link generated successfully!</p>
                <div className="flex items-center space-x-2">
                  <Input value={paymentLink} readOnly className="text-xs" />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleCopyLink}
                  >
                    Copy
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Send this link to the customer to complete payment
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    // Force generate a new link
                    generateLink.mutate(true);
                  }}
                  disabled={generateLink.isPending}
                  className="w-full mt-2"
                >
                  {generateLink.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-2 h-4 w-4" />
                  )}
                  Generate New Link
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {method === "INVOICE" && (
            <>
              {customerPaymentTerms &&
              customerPaymentTerms.paymentTerms.length > 0 ? (
                <>
                  <div className="space-y-2">
                    <Label>NET Payment Terms</Label>
                    <Select
                      value={selectedPaymentTermsId || ""}
                      onValueChange={setSelectedPaymentTermsId}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select payment terms" />
                      </SelectTrigger>
                      <SelectContent>
                        {customerPaymentTerms.paymentTerms.map((term) => (
                          <SelectItem key={term.id} value={term.id}>
                            <div className="flex flex-col">
                              <span>{term.name}</span>
                              {term.creditLimit && (
                                <span className="text-xs text-muted-foreground">
                                  Credit Limit:{" "}
                                  {formatCurrency(term.creditLimit)}
                                </span>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selectedPaymentTermsId && (
                    <Alert>
                      <DollarSign className="h-4 w-4" />
                      <AlertDescription className="space-y-2">
                        <p className="font-medium">NET Terms Summary:</p>
                        {(() => {
                          const selectedTerm =
                            customerPaymentTerms.paymentTerms.find(
                              (t) => t.id === selectedPaymentTermsId
                            );
                          if (!selectedTerm) return null;

                          return (
                            <>
                              <div className="text-sm space-y-1">
                                <p>
                                  Payment Due: {selectedTerm.paymentDueDays}{" "}
                                  days
                                </p>
                                {selectedTerm.creditLimit && (
                                  <>
                                    <p>
                                      Credit Limit:{" "}
                                      {formatCurrency(selectedTerm.creditLimit)}
                                    </p>
                                    <p>
                                      Available Credit:{" "}
                                      {formatCurrency(
                                        customerPaymentTerms.availableCredit
                                      )}
                                    </p>
                                    <p>
                                      Outstanding Balance:{" "}
                                      {formatCurrency(
                                        customerPaymentTerms.outstandingBalance
                                      )}
                                    </p>
                                  </>
                                )}
                                <p className="text-xs text-muted-foreground mt-2">
                                  Invoiced on a {selectedTerm.dayInterval}-day
                                  billing cycle
                                </p>
                              </div>
                            </>
                          );
                        })()}
                      </AlertDescription>
                    </Alert>
                  )}
                </>
              ) : (
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    This customer does not have NET terms enabled. Please
                    contact support to enable NET terms.
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}

          {method === "CREDIT" && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Credit pays the full outstanding balance (
                {formatCurrency(order.balanceCents)}) — partial credit payments
                are not supported. Fails if the customer's credit doesn't cover
                it.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={processPayment.isPending || generateLink.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleProcessPayment}
            disabled={
              processPayment.isPending ||
              generateLink.isPending ||
              recordManualPayment.isPending ||
              recordNetTermsPayment.isPending ||
              (method === "SAVED_CARD" && paymentLink !== null) ||
              (method === "INVOICE" && !selectedPaymentTermsId) ||
              (method === "CREDIT" ? order.balanceCents <= 0 : amount <= 0)
            }
          >
            {(processPayment.isPending ||
              generateLink.isPending ||
              recordManualPayment.isPending ||
              recordNetTermsPayment.isPending) && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            {method === "SAVED_CARD"
              ? paymentLink
                ? "Payment Link Ready"
                : "Generate Payment Link"
              : method === "INVOICE"
                ? "Apply NET Terms"
                : "Process Payment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
